import { getConfiguration, getDirectApi } from 'mytours-read-common'
import { getUAParser } from '../../services/uaParser'
import { captureApiException, isNetworkError } from '../api/errors'

// Deep Linking Apple Enabled, declared in views/layouts/mobile_web.html.erb
declare const deepLinkingAppleEnabled: boolean

interface NetworkInformation {
	type: 'bluetooth' | 'cellular' | 'ethernet' | 'none' | 'wifi' | 'wimax' | 'other' | 'unknown'
}

declare global {
	interface Navigator {
		connection?: NetworkInformation
		mozConnection?: NetworkInformation
		webkitConnection?: NetworkInformation
	}
}

function getRegion(language: string): string {
	try {
		const locale = new Intl.Locale(language)
		return locale.region || locale.maximize().region || ''
	} catch {
		return ''
	}
}

// Guarded like `getRegion`, so a browser reporting no language still records its visit.
function getLanguage(language: string): string {
	try {
		return new Intl.Locale(language).language
	} catch {
		return ''
	}
}

function harmonizeConnectionType(type: NetworkInformation['type']): 'cellular' | 'wifi' | '' {
	switch (type) {
		case 'wimax':
			return 'cellular'
		case 'cellular':
			return 'cellular'
		case 'wifi':
			return 'wifi'
		default:
			return ''
	}
}

function storeDeferredDeepLinkingURL() {
	const platform = getConfiguration().getPlatform()
	const deviceUUID = getConfiguration().getDeviceUUID()

	const result = getUAParser().getResult()
	const isAndroid = result.os.name === 'Android'

	const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection

	// Nobody waits for this, and it fires on every navigation, so offline it must not reach Sentry.
	getDirectApi()
		.storeDeepLinkURL(platform, deviceUUID, {
			locale: isAndroid ? '' : navigator.language.replace('-', '_'),
			screen_scale: window.devicePixelRatio.toFixed(1),
			screen_height: isAndroid ? undefined : window.outerHeight,
			country: isAndroid ? '' : getRegion(navigator.language),
			os_name: result.os.name,
			language: isAndroid ? '' : getLanguage(navigator.language),
			os_version: isAndroid ? '' : result.os.version,
			brand_name: isAndroid ? '' : result.device.vendor,
			screen_width: window.outerWidth,
			connection_type: isAndroid ? harmonizeConnectionType(connection?.type) : '',
			model_name: isAndroid ? '' : result.device.model,
			url: window.location.href,
		})
		.catch(error => {
			if (isNetworkError(error)) return

			captureApiException(error, 'storeDeepLinkURL')
		})
}

export function storeDeferredDeepLinkingURLIfNeeded() {
	if (!deepLinkingAppleEnabled) return

	try {
		storeDeferredDeepLinkingURL()
	} catch (error) {
		// Reading configuration and building the request both throw before any promise exists, and
		// this runs in componentDidMount — so an escape would reach the boundary and replace the page.
		captureApiException(error, 'storeDeepLinkURL')
	}
}
